import React, { useMemo, useState } from 'react';
import CashFlowStatusBadge from './CashFlowStatusBadge';
import { formatFinancialUnitValue } from '../utils/financialUnit';

const STORAGE_KEY = 'finlytics.cfBenchPanel.expanded';

function readExpandedPreference() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw == null) return null;
        const parsed = JSON.parse(raw);
        if (parsed === true || parsed === false) return parsed;
        return null;
    } catch {
        return null;
    }
}

function persistExpandedPreference(expanded) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(Boolean(expanded)));
    } catch {
        /* ignore quota / private-mode failures */
    }
}

function formatBenchmarkValue(value, unit) {
    if (value == null || !Number.isFinite(value)) return null;
    if (unit === 'percentage' || unit === '%') return `${Number(value.toFixed(1))}%`;
    if (unit === 'pp') return `${Number(value.toFixed(2))} pp`;
    if (unit === 'multiple' || unit === 'x') return `${Number(value.toFixed(2))}x`;
    const formatted = formatFinancialUnitValue(value, unit);
    return formatted || null;
}

function BenchmarkCard({ card, includeStability }) {
    const primary = formatBenchmarkValue(card.primaryValue, card.unit);
    const latest = formatBenchmarkValue(card.latestValue, card.unit);
    const showLatest = latest && latest !== primary;
    const warnings = (card.warnings || []).filter(Boolean);
    const stability = includeStability ? card.fcfStability : null;

    return (
        <article className="fin-bench-card">
            <div className="fin-bench-card-head">
                <h5 className="fin-bench-card-title">{card.label}</h5>
                <CashFlowStatusBadge status={card.status} />
            </div>
            {card.status !== 'diagnosticOnly' && (
                <p className="fin-bench-values">
                    {primary ? (
                        <>
                            <strong>{primary}</strong>
                            <span> {card.windowUsed || 'multi-year'}</span>
                        </>
                    ) : (
                        <span>No scored multi-year value</span>
                    )}
                    {showLatest ? <span className="fin-bench-latest"> · Latest: {latest}</span> : null}
                </p>
            )}
            {card.ruleText ? <p className="fin-bench-rule">{card.ruleText}</p> : null}
            {card.interpretation ? <p className="fin-bench-interpretation">{card.interpretation}</p> : null}
            {stability && stability.validCfoYears ? (
                <p className="fin-bench-stability">
                    FCF stability: positive in {stability.positiveFcfYears} of {stability.validCfoYears} recent CFO-positive years
                    {stability.ratio != null ? ` (${Math.round(stability.ratio * 100)}%)` : ''}.
                </p>
            ) : null}
            {card.conservativeCoverage != null && Number.isFinite(card.conservativeCoverage) ? (
                <p className="fin-bench-conservative">
                    Conservative FCF coverage: {Number(card.conservativeCoverage.toFixed(2))}x
                </p>
            ) : null}
            {warnings.length > 0 && (
                <ul className="fin-bench-warnings">
                    {warnings.map((warning) => (
                        <li key={warning}>{warning}</li>
                    ))}
                </ul>
            )}
        </article>
    );
}

export default function CashFlowBenchmarkPanel({ result }) {
    const [showAll, setShowAll] = useState(false);
    const [storedExpanded, setStoredExpanded] = useState(readExpandedPreference);
    const profile = result?.profile;
    const cards = result?.cards;
    const defaultCardIds = result?.defaultCardIds;
    const includeStability = Boolean(result?.includeStability);

    const defaultCards = useMemo(() => {
        const list = Array.isArray(cards) ? cards : [];
        const ids = new Set(defaultCardIds || []);
        const preferred = list.filter((card) => ids.has(card.metricId));
        return (preferred.length ? preferred : list).slice(0, 2);
    }, [cards, defaultCardIds]);

    const extraCards = useMemo(() => {
        const list = Array.isArray(cards) ? cards : [];
        const visible = new Set(defaultCards.map((card) => card.metricId));
        return list.filter((card) => !visible.has(card.metricId));
    }, [cards, defaultCards]);

    const heuristicExpanded = defaultCards.some(
        (card) => card.status === 'weak' || card.status === 'insufficientData',
    );
    const panelOpen = storedExpanded === null ? heuristicExpanded : storedExpanded;

    const handlePanelToggle = (event) => {
        if (!event.isTrusted) return;
        const nextOpen = event.currentTarget.open;
        setStoredExpanded(nextOpen);
        persistExpandedPreference(nextOpen);
    };

    if (!result || !profile || !cards?.length) return null;

    const canonicalLabel = profile.sector.label;
    const rawSector = profile.sector.rawSector;
    const showSource = Boolean(rawSector)
        && (profile.sector.unmapped || String(rawSector).trim().toLowerCase() !== canonicalLabel.toLowerCase());

    return (
        <details className="fin-bench" open={panelOpen} onToggle={handlePanelToggle}>
            <summary className="fin-bench-summary">
                Sector benchmark — {canonicalLabel}
            </summary>
            <div className="fin-bench-body">
                <div className="fin-bench-meta">
                    <span className="fin-bench-badge">Initial heuristic</span>
                    <span>Source: {result.source || 'heuristic'}</span>
                    <span>Window: {profile.primaryWindowLabel}</span>
                    {showSource ? <span>Source sector: {rawSector}</span> : null}
                </div>
                <p className="fin-bench-methodology">{result.methodologyNote || profile.methodologyNote}</p>
                <div className="fin-bench-cards">
                    {defaultCards.map((card) => (
                        <BenchmarkCard
                            key={card.metricId}
                            card={card}
                            includeStability={includeStability}
                        />
                    ))}
                </div>
                {extraCards.length > 0 && (
                    <div className="fin-bench-more">
                        <button
                            type="button"
                            className="fin-bench-more-toggle"
                            onClick={() => setShowAll((open) => !open)}
                        >
                            {showAll ? 'Hide extra benchmark details' : 'View all benchmark details'}
                        </button>
                        {showAll && (
                            <div className="fin-bench-cards fin-bench-cards--extra">
                                {extraCards.map((card) => (
                                    <BenchmarkCard
                                        key={card.metricId}
                                        card={card}
                                        includeStability={includeStability}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </details>
    );
}
